/**
 * Role reference data for the OEM+ Help section: the role chips, the "who works
 * where" navigation map, and the per-role guide cards.
 *
 * Sources (all in `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`):
 * - `const ROLE={ … }`      line 3723 — 6 roles, `[label, colour]` tuples.
 * - `const NAVMAP=[ … ]`    line 3729 — 7 sections, 29 page tiles in total.
 * - `const ROLEGUIDE=[ … ]` line 3875 — 6 role cards, 5 pages each.
 *
 * WHY NAVMAP LIVES HERE: it is navigation-shaped, but it is reference data for
 * the Help section's "navmap" grid (`renderHelp()` at line ~3835) rather than
 * the app's real sidebar, and every entry is keyed by the role chips defined in
 * `ROLE` — the two cannot be typed apart. The chrome lane owns the real nav;
 * this is the Help page's annotated tour of it, so it is carried with the roles
 * as the task brief allows.
 *
 * Porting decisions
 * - `ROLE` is a two-element tuple `[label, colour]`; field names come from
 *   `roleChip()` / `roleDots()` (lines 3725–3726), which use `[0]` as the chip
 *   text and `title` attribute and `[1]` as the chip's `--rc` CSS colour.
 * - NAVMAP tuple `[key, name, col, what, who, items]` and its item tuple
 *   `[t, q, roles, go]` are named from `renderHelp()`: `t` is the page title,
 *   `q` is "what it answers", `roles` drives the role dots, `go` was the
 *   `onclick` payload.
 * - ROLEGUIDE tuple `[k, name, org, what, pages, day]` and its page tuple
 *   `[t, q, go]` are named from the role-card renderer at line ~3935; `day` is
 *   printed under the eyebrow "A typical morning".
 * - HANDLERS: every `go` in both constants was a bare `show(view[, sub])` call,
 *   so all 29 NAVMAP items and all 30 ROLEGUIDE pages carry an `OemAction` built
 *   with `act()`. None were dropped; no `audit()` / `ask()` / `openDoc()`
 *   payloads appear in these three constants.
 * - ROLE ids differ from persona ids: the Aftermarket Manager is `'after'` here
 *   and `'aftermarket'` in `personas.ts`. Both are kept exactly as the demo has
 *   them — `AFTERMARKET_ROLE_TO_PERSONA` records the correspondence.
 * - No markup was stripped: none of these strings contain HTML.
 * - Colours are the demo's literal hex values, kept as data (the chrome lane
 *   decides whether to map them onto tokens).
 *
 * Data Schema layer — pure data, no behaviour.
 */

import { act, type OemAction } from './action';

// ---------------------------------------------------------------------------
// ROLE — the chips
// ---------------------------------------------------------------------------

/** Role keys used by the chips, the nav map and the role guide. */
export const ROLE_IDS = ['rel', 'rsm', 'tech', 'parts', 'planner', 'after'] as const;

/** A role key, e.g. `'planner'`. */
export type RoleId = (typeof ROLE_IDS)[number];

/** Chip definition for one role. */
export interface Role {
  /** Chip text and `title` attribute. */
  readonly label: string;
  /** Chip accent colour (`--rc`), as authored in the demo. */
  readonly colour: string;
}

/** The six role chips. */
export const ROLE: Readonly<Record<RoleId, Role>> = {
  rel: { label: 'Fleet Reliability Lead', colour: '#2F5FA8' },
  rsm: { label: 'Regional Service Manager', colour: '#A3123F' },
  tech: { label: 'Field Service Technician', colour: '#C8500A' },
  parts: { label: 'Parts & Logistics Coordinator', colour: '#0E7C86' },
  planner: { label: 'Site Maintenance Planner', colour: '#5E35B1' },
  after: { label: 'Aftermarket Manager', colour: '#2E7D4F' },
};

/**
 * The Aftermarket Manager is `'after'` in `ROLE`/`NAVMAP`/`ROLEGUIDE` but
 * `'aftermarket'` in `PERSONAS`. Recorded so a scene can bridge the two.
 */
export const AFTERMARKET_ROLE_TO_PERSONA = { role: 'after', persona: 'aftermarket' } as const;

// ---------------------------------------------------------------------------
// NAVMAP — section → what it is, who lives there, and its pages
// ---------------------------------------------------------------------------

/** One page tile inside a nav-map section. */
export interface NavMapItem {
  /** Page title. */
  readonly title: string;
  /** What the page answers. */
  readonly question: string;
  /** Roles that use it; rendered as coloured dots. */
  readonly roles: readonly RoleId[];
  /** Where the tile navigates. */
  readonly action: OemAction;
}

/** One column of the Help section's nav map. */
export interface NavMapSection {
  /** Section key; the header navigates to `show('section', key)`. */
  readonly key: string;
  /** Section name. */
  readonly name: string;
  /** Section accent colour (`--sec`), as authored in the demo. */
  readonly colour: string;
  /** One line on what the section is for. */
  readonly what: string;
  /** Roles that live in this section; rendered as chips. */
  readonly who: readonly RoleId[];
  readonly items: readonly NavMapItem[];
}

/** The seven Help nav-map sections, in demo order. */
export const NAVMAP: readonly NavMapSection[] = [
  {
    key: 'home',
    name: 'Home',
    colour: '#1C3552',
    what: 'Where the morning starts and where the whole model is visible at once.',
    who: ['after', 'rsm', 'rel'],
    items: [
      {
        title: 'Asset360',
        question: 'The control tower, the ten modules, the five loops, the agents and the systems underneath',
        roles: ['after', 'rsm'],
        action: act('asset360'),
      },
      {
        title: 'Sites map',
        question: 'Where the fleet is: six mines, condition, availability and open work',
        roles: ['rel', 'after'],
        action: act('overview'),
      },
      {
        title: 'Customers',
        question: 'Who each site is, the contract behind it, the contacts and their scores',
        roles: ['rsm', 'after'],
        action: act('customers'),
      },
      {
        title: 'Job board',
        question: 'Every open job at the module it has reached, by owner',
        roles: ['rsm', 'planner'],
        action: act('jobboard'),
      },
      {
        title: 'Fleet health',
        question: 'Every machine by mine with its key metric over twelve days',
        roles: ['rel', 'tech'],
        action: act('fleet'),
      },
      {
        title: 'S.O.S report',
        question: 'The 06:00 service operations summary — hotspots by site and class',
        roles: ['rsm', 'after'],
        action: act('sos'),
      },
      {
        title: 'Approvals',
        question: 'Everything an agent has drafted and is waiting on a person for',
        roles: ['rsm', 'planner'],
        action: act('approvals'),
      },
      {
        title: 'Cost summary',
        question: 'Every dollar traced to a work order, machine, crew and supplier',
        roles: ['after', 'rsm'],
        action: act('costs'),
      },
    ],
  },
  {
    key: 'issues',
    name: 'Issues',
    colour: '#C62828',
    what: 'What is wrong right now and what it costs to leave it.',
    who: ['rsm', 'planner', 'tech'],
    items: [
      {
        title: 'Live',
        question: 'Alerts and incidents together, each with the cost of doing nothing',
        roles: ['rsm', 'planner'],
        action: act('issues'),
      },
      {
        title: 'Alerts',
        question: 'The evidence, the window, the confidence and the agent chain per alert',
        roles: ['rel', 'planner'],
        action: act('alerts'),
      },
      {
        title: 'Incidents',
        question: 'Closed incidents, replayed through the agent chain on the data that existed',
        roles: ['rel', 'after'],
        action: act('incident'),
      },
    ],
  },
  {
    key: 'workflows',
    name: 'Workflows',
    colour: '#A3123F',
    what: 'The operating model: ten modules, and two or three steps inside each.',
    who: ['after', 'rsm', 'parts'],
    items: [
      {
        title: 'The ten modules',
        question: 'Asset base through to closeout and value, each with what is open in it now',
        roles: ['after'],
        action: act('section', 'workflows'),
      },
      {
        title: 'The twenty-two steps',
        question: 'Each module page opens into its own steps — one page per question',
        roles: ['after', 'parts'],
        action: act('wf', 'intake'),
      },
      {
        title: 'Case-to-closure trace',
        question: 'One case end to end with what happened at each step',
        roles: ['rsm', 'planner'],
        action: act('jobrun'),
      },
    ],
  },
  {
    key: 'loops',
    name: 'Feedback loops',
    colour: '#2E7D4F',
    what: 'What makes the model get better with every job it closes.',
    who: ['rel', 'after'],
    items: [
      {
        title: 'Component lifecycle & rebuild',
        question: 'Serialised components, exchange pools and core returns',
        roles: ['parts', 'rel'],
        action: act('loops', 'component'),
      },
      {
        title: 'Warranty, quality & campaigns',
        question: 'Claims against the evidence, clusters, bulletins and campaigns',
        roles: ['rsm', 'rel'],
        action: act('loops', 'warranty'),
      },
      {
        title: 'Fleet learning',
        question: 'Rules now running across every machine of a type, each from one job',
        roles: ['rel', 'after'],
        action: act('loops', 'learning'),
      },
      {
        title: 'Commercial opportunity',
        question: 'Renewals, upgrade candidates and what a job reveals',
        roles: ['rsm', 'after'],
        action: act('loops', 'commercial'),
      },
      {
        title: 'Demand & supply planning',
        question: 'Forecast demand positioned across the network',
        roles: ['parts', 'after'],
        action: act('loops', 'planning'),
      },
    ],
  },
  {
    key: 'assets',
    name: 'Assets',
    colour: '#6A4FB6',
    what: 'Every machine in the population, division by division.',
    who: ['rel', 'tech'],
    items: [
      {
        title: 'Surface Drilling',
        question: 'Top-hammer and blasthole drills on the benches',
        roles: ['rel', 'tech'],
        action: act('surface'),
      },
      {
        title: 'Underground Drilling',
        question: 'Development jumbos and production longhole rigs',
        roles: ['rel', 'tech'],
        action: act('drilling'),
      },
      {
        title: 'Underground Load & Haul',
        question: 'Loaders and trucks — diesel, battery-electric and autonomous',
        roles: ['rel', 'tech'],
        action: act('loadhaul'),
      },
      {
        title: 'A machine page',
        question: '360° view, condition register, history, cost and its Maximo record',
        roles: ['tech', 'rel'],
        action: act('machine', 'L-33'),
      },
    ],
  },
  {
    key: 'sites',
    name: 'Mine sites',
    colour: '#0E7C86',
    what: 'Everything about a place, not just the machines on it.',
    who: ['planner', 'tech', 'parts'],
    items: [
      {
        title: 'A site page',
        question: 'Fleet, people, parts, ground, services, access, open work and documents',
        roles: ['planner', 'tech'],
        action: act('sites', 'GD'),
      },
      {
        title: 'Parts & supply on site',
        question: 'What the store holds, what the DC holds and how long the road takes',
        roles: ['parts'],
        action: act('sites', 'parts'),
      },
      {
        title: 'Ground, services & access',
        question: 'Ventilation, dewatering, roster and the road in',
        roles: ['planner', 'tech'],
        action: act('sites', 'conditions'),
      },
    ],
  },
  {
    key: 'integration',
    name: 'Integration',
    colour: '#C8800A',
    what: 'What the workspace is built on and what it reads.',
    who: ['after', 'rel'],
    items: [
      {
        title: 'Data',
        question: 'Every file in the store, in the format its system produces',
        roles: ['after'],
        action: act('data'),
      },
      {
        title: 'Connectors',
        question: 'What each system feeds in and how fresh it is',
        roles: ['after'],
        action: act('connectors'),
      },
      {
        title: 'Agents',
        question: 'The sixteen specialists, what each reads and what each writes',
        roles: ['after', 'rsm'],
        action: act('agents'),
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// ROLEGUIDE — one card per role
// ---------------------------------------------------------------------------

/** A page a role opens, as listed on its guide card. */
export interface RoleGuidePage {
  /** Page title, verbatim (module pages keep their `'04 → Root cause analysis'` form). */
  readonly title: string;
  /** What that page gives them. */
  readonly question: string;
  /** Where the button navigates. */
  readonly action: OemAction;
}

/** One role card in the Help section. */
export interface RoleGuide {
  readonly id: RoleId;
  /** Role name; the avatar is its initials. */
  readonly name: string;
  /** Employer and where they sit. */
  readonly org: string;
  /** What they own. */
  readonly what: string;
  readonly pages: readonly RoleGuidePage[];
  /** The "A typical morning" line. */
  readonly day: string;
}

/** The six role guide cards, in demo order. */
export const ROLEGUIDE: readonly RoleGuide[] = [
  {
    id: 'rel',
    name: 'Fleet Reliability Lead',
    org: 'Meridian Equipment',
    what: 'Owns condition and cost per hour across the whole base. Lives in the trend, not the job.',
    pages: [
      {
        title: 'Fleet health',
        question: 'every machine by mine, twelve days of its key metric',
        action: act('fleet'),
      },
      {
        title: 'Alerts',
        question: 'the evidence and confidence behind each prediction',
        action: act('alerts'),
      },
      {
        title: '04 → Root cause analysis',
        question: 'failure modes across the fleet and the repeats',
        action: act('wf', 'rca'),
      },
      {
        title: 'Fleet learning',
        question: 'the rules that came out of single jobs',
        action: act('loops', 'learning'),
      },
      {
        title: '01 → Machine life & meters',
        question: 'which units carry the hours and what is past interval',
        action: act('wf', 'life'),
      },
    ],
    day: 'Opens Fleet health each morning, then Root cause analysis weekly to see whether a failure mode is a fleet problem or one machine.',
  },
  {
    id: 'rsm',
    name: 'Regional Service Manager',
    org: 'Meridian Equipment',
    what: 'Owns the customer commitment and the people. Approves what the agents draft.',
    pages: [
      {
        title: 'Approvals',
        question: 'everything drafted and waiting on a person',
        action: act('approvals'),
      },
      {
        title: 'Live',
        question: 'what is wrong now and what it costs to leave it',
        action: act('issues'),
      },
      {
        title: 'Job board',
        question: 'every open job at the module it has reached',
        action: act('jobboard'),
      },
      {
        title: '05 → Entitlement & warranty',
        question: 'who pays, decided before the work is planned',
        action: act('wf', 'entitle'),
      },
      {
        title: 'Customers',
        question: 'contacts, contracts and what they scored',
        action: act('customers'),
      },
    ],
    day: 'Starts in Approvals, clears the queue, then Live for anything that has moved overnight; uses Entitlement before committing a job.',
  },
  {
    id: 'tech',
    name: 'Field Service Technician',
    org: 'Meridian Equipment · on site',
    what: 'Holds the work package. Needs the steps, the torques, the isolation and the parts.',
    pages: [
      {
        title: '09 → Work package execution',
        question: 'the steps, torque values, isolation and the kit',
        action: act('wf', 'pack'),
      },
      {
        title: 'A machine page',
        question: '360° view, condition register and history',
        action: act('machine', 'L-33'),
      },
      {
        title: '04 → Remote diagnosis',
        question: 'an expert on the machine without the flight',
        action: act('wf', 'remote4'),
      },
      {
        title: 'Mine site → ground & access',
        question: 'ventilation, roster, the road in',
        action: act('sites', 'conditions'),
      },
      {
        title: 'Fleet health',
        question: 'the machine trend behind the job',
        action: act('fleet'),
      },
    ],
    day: 'Opens the package for the shift, pulls the procedure and the kit list, escalates to remote diagnosis rather than waiting for a visit.',
  },
  {
    id: 'parts',
    name: 'Parts & Logistics Coordinator',
    org: 'Meridian Equipment · distribution centre',
    what: 'Owns whether the part lands inside the window and what holding it costs.',
    pages: [
      {
        title: '07 → Pipeline & expediting',
        question: 'every requisition, need-by against ETA',
        action: act('wf', 'pipe'),
      },
      {
        title: '07 → Stock & replenishment',
        question: 'what is below minimum and whether a transfer beats an order',
        action: act('wf', 'stock'),
      },
      {
        title: '02 → Predicted parts demand',
        question: 'what the next thirty days will consume',
        action: act('wf', 'forecast'),
      },
      {
        title: '07 → Parts book',
        question: 'the right number for the serial range',
        action: act('wf', 'book'),
      },
      {
        title: 'Demand & supply planning',
        question: 'the network position behind it',
        action: act('loops', 'planning'),
      },
    ],
    day: 'Works the pipeline first, then the below-minimum list, and checks predicted demand before committing the next replenishment.',
  },
  {
    id: 'planner',
    name: 'Site Maintenance Planner',
    org: 'Customer side, on the mine',
    what: 'Owns the shift plan and the machine release. Approves what touches their fleet.',
    pages: [
      {
        title: '06 → Service plan & window',
        question: 'due work against the shift plan and the access rules',
        action: act('wf', 'plan'),
      },
      {
        title: '06 → Promise & readiness',
        question: 'the four checks before a date is offered',
        action: act('wf', 'ready'),
      },
      {
        title: 'Approvals',
        question: 'the drafts that affect their machines',
        action: act('approvals'),
      },
      {
        title: 'Their mine site page',
        question: 'fleet, people, parts and conditions in one place',
        action: act('sites', 'MI'),
      },
      {
        title: 'Live',
        question: 'what is wrong on their fleet this morning',
        action: act('issues'),
      },
    ],
    day: 'Checks Live for their site, then the plan and the readiness scores, and approves or holds the drafts that touch their machines.',
  },
  {
    id: 'after',
    name: 'Aftermarket Manager',
    org: 'Meridian Equipment',
    what: 'Owns the model and the P&L. Wants the shape of the whole thing, then the exception.',
    pages: [
      {
        title: 'Asset360',
        question: 'the control tower, the modules and the loops',
        action: act('asset360'),
      },
      {
        title: 'Workflows',
        question: 'ten modules with what is open in each',
        action: act('section', 'workflows'),
      },
      {
        title: 'Cost summary',
        question: 'cost to serve by site, category and machine',
        action: act('costs'),
      },
      {
        title: '10 → Customer outcome & CSS',
        question: 'what customers scored and said',
        action: act('wf', 'outcome'),
      },
      {
        title: 'Commercial opportunity',
        question: 'renewals and upgrade candidates',
        action: act('loops', 'commercial'),
      },
    ],
    day: 'Reads the tower for the eight measures, drops into whichever one is amber or red, and uses the loops to see what is being learned.',
  },
];
